import * as api from "./client.js";

/**
 * AI submission parser configuration and stats.
 *
 * Every endpoint here is proxied by staff-bff under `/api/v1/submission/ai-parser/*` and gated on
 * the single `submission.config` permission — the schemas are what the worker spends money
 * against and the markdown templates execute EJS server-side, so reading the stats and editing
 * the configuration are not separately gated.
 *
 * See docs/ai-submission-parser/DESIGN.md.
 */

const BASE = "/v1/submission/ai-parser";

// --- Shared vocabularies (mirror AiProviderSchema / AiThinkingLevelSchema in submission-api) ---

export type AiProvider = "azure-openai" | "azure-anthropic";

export const AI_PROVIDERS: { value: AiProvider; label: string }[] = [
  { value: "azure-openai", label: "Azure OpenAI" },
  { value: "azure-anthropic", label: "Azure Anthropic" },
];

export type AiThinkingLevel = "none" | "minimal" | "low" | "medium" | "high" | "xhigh" | "max";

export const AI_THINKING_LEVELS: AiThinkingLevel[] = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];

/**
 * What one provider's model listing looks like.
 *
 * `available: false` is a normal answer, not an error to swallow: the listing endpoint is a
 * convenience over whatever the Azure resource happens to expose, and a resource that cannot be
 * listed (no permission, not configured, transient failure) still has perfectly usable deployment
 * names. `error` is the sentence to show the user in that case, and the Model field falls back to
 * free text so the schema can still be saved.
 */
/**
 * One option in the Model picker.
 *
 * `id` is the value a schema stores and the worker sends as `model` — on Azure a deployment name,
 * which is whatever whoever created it typed. `baseModel` is the model behind that deployment
 * where the listing names one, shown next to the id because a dropdown of
 * `l500-dev-platform-core-openai-gpt-4o-mini` on its own is unreadable. It is never the value.
 */
export type AiListedModel = {
  id: string;
  baseModel: string | null;
  /** Which listing answered: live deployments, or the base-model fallback. */
  source: "deployments" | "models";
};

export type AiProviderModels = {
  provider: AiProvider;
  models: AiListedModel[];
  available: boolean;
  error: string | null;
};

export type AiModelsResponse = { providers: AiProviderModels[]; requestId?: string | null };

export type AiParseRunStatus = "queued" | "running" | "succeeded" | "invalid" | "failed";

export const AI_PARSE_RUN_STATUSES: AiParseRunStatus[] = [
  "queued",
  "running",
  "succeeded",
  "invalid",
  "failed",
];

// --- ai_schemas ---

/**
 * How sensitive what a schema or template produces is. The worker writes the file into a folder
 * of this name inside the run, so the value is a path segment as well as a label.
 */
export const DATA_CLASSIFICATIONS = ["public", "internal", "confidential", "restricted"] as const;

export type DataClassification = (typeof DATA_CLASSIFICATIONS)[number];

export const DATA_CLASSIFICATION_LABELS: Record<DataClassification, string> = {
  public: "Public",
  internal: "Internal",
  confidential: "Confidential",
  restricted: "Restricted",
};

export type AiSchema = {
  id: string;
  name: string;
  slug: string;
  jsonSchema: unknown;
  systemPrompt: string | null;
  provider: string;
  model: string;
  thinkingLevel: string | null;
  temperature: number | null;
  dataClassification: DataClassification;
  enabled: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
};

export type CreateAiSchemaInput = {
  name: string;
  slug: string;
  jsonSchema: unknown;
  systemPrompt?: string | null;
  provider: AiProvider;
  model: string;
  thinkingLevel?: AiThinkingLevel | null;
  temperature?: number | null;
  dataClassification?: DataClassification;
  enabled?: boolean;
};

/** PATCH is a patch, not a replacement — every field is optional and an empty body is a no-op. */
export type UpdateAiSchemaInput = Partial<CreateAiSchemaInput>;

/**
 * One Ajv complaint from a test run: the JSON Pointer it failed at, and why.
 *
 * `AiParseRun.validationErrorPaths` carries paths alone, because a stored message can quote the
 * offending value. A test run stores nothing, so it can afford the message — and the message is
 * what tells a schema author whether their schema or the model is wrong.
 */
export type AiSchemaTestIssue = { path: string; message: string };

/**
 * What POST `schemas/:id/test` answers with.
 *
 * `document` is THE EXTRACTED CONTENT, and it is the only place in this API that returns any. It
 * is deliberate and it is bounded: the file was uploaded by the person reading the result, from
 * their own machine, and the server persists nothing — no blob, no parse run, no row of any kind.
 * See the controller comment in submission-api for the reasoning before changing either end.
 */
export type AiSchemaTestResult = {
  aiSchemaId: string;
  aiSchemaName: string;
  aiSchemaSlug: string;
  fileName: string;
  provider: string;
  model: string;
  thinkingLevel: string | null;
  valid: boolean;
  /** True when the model's raw output had to be repaired before it would parse. */
  repaired: boolean;
  issues: AiSchemaTestIssue[];
  /** Set only when nothing parseable came back; `document` is then null. */
  parseError: string | null;
  fieldsTotal: number;
  fieldsPopulated: number;
  /** Summed across both attempts — a retry costs real money and is reported as spent. */
  promptTokens: number;
  completionTokens: number;
  /** "length" means the model hit its output ceiling and the extraction may be truncated. */
  finishReason: string;
  attempt: number;
  durationMs: number;
  /**
   * "sample" when the uploaded document carried the SAMPLE watermark, otherwise null. Output-only:
   * it is derived from the document, never chosen, which is why it is not in `DATA_CLASSIFICATIONS`.
   * A sample `document` also carries a top-level `"classification": "SAMPLE"`, as a written JSON would.
   */
  classification: "sample" | null;
  document: unknown;
};

export type AiSchemaTestResponse = { test: AiSchemaTestResult; requestId?: string | null };

export type AiTestDocument = {
  submissionEntryFileId: string;
  fileName: string;
  submissionId: string;
  firmName: string | null;
  sharepointFileName: string | null;
  label: string | null;
  uploadedAt: string | null;
};

export type AiTestDocumentListResponse = {
  documents: AiTestDocument[];
  requestId?: string | null;
};

export type AiSchemaListResponse = { schemas: AiSchema[]; requestId?: string | null };
export type AiSchemaSingleResponse = { schema: AiSchema; requestId?: string | null };
export type DeletedResponse = { deleted: true; requestId?: string | null };

// --- ai_markdown_templates ---

export type AiMarkdownTemplate = {
  id: string;
  aiSchemaId: string;
  name: string;
  filenameFormat: string;
  body: string;
  dataClassification: DataClassification;
  enabled: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
};

export type CreateAiMarkdownTemplateInput = {
  aiSchemaId: string;
  name: string;
  filenameFormat: string;
  body: string;
  dataClassification?: DataClassification;
  enabled?: boolean;
};

export type UpdateAiMarkdownTemplateInput = Partial<CreateAiMarkdownTemplateInput>;

export type AiMarkdownTemplateListResponse = {
  markdownTemplates: AiMarkdownTemplate[];
  requestId?: string | null;
};

export type AiMarkdownTemplateSingleResponse = {
  markdownTemplate: AiMarkdownTemplate;
  requestId?: string | null;
};

// --- ai_parse_runs ---

/**
 * A run as the API returns it. METRICS ONLY: counts, statuses, timings, token usage, the blob
 * path the output was written to, and JSON-Pointer paths of the fields Ajv rejected. No parsed
 * field value is on this shape, and none is stored to return.
 */
/**
 * Live counts for a run that has not settled, published by the worker while the model is
 * generating. Null on every settled run, and null on a running one the worker has not reported
 * on yet — so treat its absence as "no information", not as "nothing has happened".
 */
export type AiParseRunProgress = {
  attempt: number;
  /** The document measured before the call; Azure only reports the real figure at the end. */
  estimatedPromptTokens: number;
  promptTokens: number | null;
  completionTokens: number;
  /** Null until enough output has arrived to repair into a document and count. */
  fieldsPopulated: number | null;
  fieldsTotal: number;
  tokensPerSecond: number | null;
  startedAt: number;
  updatedAt: number;
};

/**
 * Where the document a run parsed came from.
 *
 * `submission` is the normal case — a firm's entry file, reached through a submission. `manual` is
 * a document a researcher uploaded from the AI Parse Runs page to exercise the pipeline: it went
 * through every enabled schema and wrote real output, but it belongs to no firm and there is no
 * submission behind it. The distinction has to survive into the table, or a test upload's numbers
 * get read as a real firm's.
 */
export type AiParseRunSource = "submission" | "manual";

export type AiParseRun = {
  id: string;
  /** Both null on a manual run, which was never attached to a submission or an entry file. */
  submissionId: string | null;
  submissionEntryFileId: string | null;
  source: AiParseRunSource;
  /** The manual upload this run came from; null on every run parsed from a real submission. */
  manualUploadId: string | null;
  /**
   * The name of the document a researcher uploaded, for a manual run only — null for a pipeline
   * run. It is the one thing a manual row can be labelled with, since it carries no submission or
   * entry file to look a name up on.
   */
  manualUploadFileName: string | null;
  aiSchemaId: string;
  aiSchemaName: string | null;
  aiSchemaSlug: string | null;
  runGroupId: string;
  status: string;
  blobPath: string | null;
  valid: boolean | null;
  validationErrorCount: number | null;
  validationErrorPaths: string[] | null;
  fieldsTotal: number | null;
  fieldsPopulated: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  /**
   * A decimal string, not a number — numeric(12,6) in Postgres, passed through as text so the
   * total does not drift through a JS float. Format it; never do arithmetic on it.
   */
  estimatedCost: string | null;
  finishReason: string | null;
  provider: string | null;
  model: string | null;
  thinkingLevel: string | null;
  publicationName: string | null;
  edition: string | null;
  submissionType: string | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  errorMessage: string | null;
  attempt: number;
  progress: AiParseRunProgress | null;
};

export type AiParseRunFilters = {
  aiSchemaId?: string;
  status?: string;
  publicationName?: string;
  edition?: string;
  /** Narrows to the runs one manual upload produced — one document across every enabled schema. */
  manualUploadId?: string;
  /** Inclusive lower bound on queuedAt, ISO 8601 with offset. */
  queuedFrom?: string;
  /** Exclusive upper bound on queuedAt, ISO 8601 with offset. */
  queuedTo?: string;
  limit?: number;
  offset?: number;
};

export type AiParseRunListResponse = {
  runs: AiParseRun[];
  pagination: { total: number; limit: number; offset: number };
  requestId?: string | null;
};

export type AiParseRunSingleResponse = { run: AiParseRun; requestId?: string | null };

export type AiParseRunAggregate = {
  totalRuns: number;
  succeededRuns: number;
  invalidRuns: number;
  failedRuns: number;
  /** succeededRuns / totalRuns as a fraction, 0 when there are no runs. */
  successRate: number;
  averageDurationMs: number | null;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  /** A decimal string, for the same reason as AiParseRun.estimatedCost. */
  totalEstimatedCost: string | null;
  averageFieldsPopulatedPercent: number | null;
};

export type AiParseRunSchemaAggregate = AiParseRunAggregate & {
  aiSchemaId: string;
  aiSchemaName: string | null;
  aiSchemaSlug: string | null;
};

export type AiParseRunStats = {
  overall: AiParseRunAggregate;
  bySchema: AiParseRunSchemaAggregate[];
};

export type AiParseRunStatsResponse = { stats: AiParseRunStats; requestId?: string | null };

// --- manual uploads ---

/**
 * The ceiling submission-api puts on one manual upload, mirrored so the form can refuse an
 * over-large file before spending a minute pushing it up only to be told 400.
 */
export const AI_PARSE_MANUAL_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

export const DOCX_MIMETYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * Optional context for a manual upload.
 *
 * These are the same three labels a real submission carries, and they exist only so a test run can
 * be found again in the filters. Nothing is looked up from them — the document is attached to no
 * firm and creates no submission, so an edition typed here names nothing.
 */
export type AiParseManualUploadFields = {
  publicationName?: string;
  edition?: string;
  submissionType?: string;
};

/** The upload row the API records, as POST `manual-uploads` answers it (202). */
export type AiParseManualUpload = {
  id: string;
  fileName: string;
  blobPath: string;
  publicationName: string | null;
  edition: string | null;
  submissionType: string | null;
  byteSize: number;
  createdAt: string;
};

export type AiParseManualUploadResponse = {
  upload: AiParseManualUpload;
  requestId?: string | null;
};

// --- reruns ---

/**
 * A single-run rerun, as POST `runs/:id/rerun` answers it (202).
 *
 * `enqueued` is the queue's answer, not the API's: a 202 means the request was understood, and
 * `enqueued: false` means nothing was actually put on the queue. Report the two differently.
 */
export type AiParseRerunResponse = {
  sourceRunId: string;
  submissionEntryFileId: string;
  aiSchemaId: string;
  enqueued: boolean;
  requestId?: string | null;
};

/**
 * The hard ceiling submission-api puts on one bulk rerun (AI_PARSE_BULK_RERUN_LIMIT).
 *
 * Mirrored here so the form can refuse an over-large number with a sentence rather than round-trip
 * it into a 400 — the API rejects rather than silently trimming, so asking for more than a job
 * holds gets nothing done at all. It is only used for the bounds the form has to know *before* it
 * asks; every count the modal shows after a dry run comes from that response's `cap`.
 */
export const AI_PARSE_BULK_RERUN_LIMIT = 1000;

/**
 * Exactly one selector: a createdFrom/createdTo range OR lastNSubmissions. Sending both is a 400,
 * because a request with two different ideas about what it is rerunning is how somebody spends a
 * few hundred model calls by accident.
 *
 * `dryRun` defaults to true server-side. It is sent explicitly on every call here anyway, so the
 * request that spends money is never the one produced by forgetting a field.
 */
export type AiParseBulkRerunInput = {
  aiSchemaId: string;
  dryRun: boolean;
  /**
   * Leave out the documents that already have a succeeded-or-invalid run for this schema.
   *
   * Defaults to `false` server-side, so it is sent explicitly on every call — see BulkRerunModal,
   * which defaults it the other way.
   */
  skipAlreadyRun?: boolean;
  limit?: number;
  /** Inclusive lower bound on the submission's createdAt, ISO 8601 with offset. */
  createdFrom?: string;
  /** Exclusive upper bound on the submission's createdAt, ISO 8601 with offset. */
  createdTo?: string;
  lastNSubmissions?: number;
};

/**
 * What the job is likely to use, projected from what this schema's previous runs actually used.
 *
 * Every field is nullable and they are null together: a schema with no run history has nothing to
 * estimate from and the API will not invent a figure. Render that as unknown — never as zero.
 */
export type AiParseBulkRerunEstimate = {
  runsSampled: number;
  averagePromptTokens: number | null;
  averageCompletionTokens: number | null;
  estimatedPromptTokens: number | null;
  estimatedCompletionTokens: number | null;
  estimatedTotalTokens: number | null;
  /** A decimal string, for the same reason as every other cost on this API. */
  estimatedCostUsd: string | null;
};

export type AiParseBulkRerunResponse = {
  /** Documents matching the selection, before `skipAlreadyRun` and before the cap. */
  total: number;
  /** What this job takes: what is left of `total` after the skip, then after the cap. */
  candidates: number;
  /**
   * How many of `total` already have a succeeded-or-invalid run for this schema. Reported whether
   * or not `skipAlreadyRun` was asked for — with the skip off it is the count of work about to be
   * paid for twice.
   */
  alreadyRun: number;
  enqueued: number;
  skipped: number;
  cap: number;
  /** True when `total` exceeded the cap, so the selection was trimmed. */
  capped: boolean;
  dryRun: boolean;
  estimate: AiParseBulkRerunEstimate;
  requestId?: string | null;
};

function runFilterParams(filters: AiParseRunFilters | undefined, withPaging: boolean): string {
  const params = new URLSearchParams();
  if (filters?.aiSchemaId) params.append("aiSchemaId", filters.aiSchemaId);
  if (filters?.status) params.append("status", filters.status);
  if (filters?.publicationName) params.append("publicationName", filters.publicationName);
  if (filters?.edition) params.append("edition", filters.edition);
  if (filters?.manualUploadId) params.append("manualUploadId", filters.manualUploadId);
  if (filters?.queuedFrom) params.append("queuedFrom", filters.queuedFrom);
  if (filters?.queuedTo) params.append("queuedTo", filters.queuedTo);
  if (withPaging && filters?.limit !== undefined) params.append("limit", String(filters.limit));
  if (withPaging && filters?.offset !== undefined) params.append("offset", String(filters.offset));
  const query = params.toString();
  return query ? `?${query}` : "";
}

async function testFailure(response: Response): Promise<Error> {
  const message = await response
    .json()
    .then(body => (body as { error?: string }).error)
    .catch(() => undefined);
  return new Error(message || `Test failed: HTTP ${response.status}`);
}

export const aiParser = {
  /**
   * The deployments each provider can currently offer, all providers in one answer.
   *
   * One call covers both providers deliberately — switching the Provider field swaps which list is
   * shown without a second request. `refresh` bypasses the server-side cache; it is what the
   * Refresh control on the form sends, for the case where a deployment was created minutes ago.
   */
  listModels(options?: { refresh?: boolean }): Promise<AiModelsResponse> {
    return api.get<AiModelsResponse>(`${BASE}/models${options?.refresh ? "?refresh=true" : ""}`);
  },

  listSchemas(filters?: { enabled?: boolean }): Promise<AiSchemaListResponse> {
    const query = filters?.enabled === undefined ? "" : `?enabled=${filters.enabled}`;
    return api.get<AiSchemaListResponse>(`${BASE}/schemas${query}`);
  },

  getSchemaById(id: string): Promise<AiSchemaSingleResponse> {
    return api.get<AiSchemaSingleResponse>(`${BASE}/schemas/${id}`);
  },

  createSchema(input: CreateAiSchemaInput): Promise<AiSchemaSingleResponse> {
    return api.post<AiSchemaSingleResponse>(`${BASE}/schemas`, input);
  },

  updateSchema(id: string, input: UpdateAiSchemaInput): Promise<AiSchemaSingleResponse> {
    return api.patch<AiSchemaSingleResponse>(`${BASE}/schemas/${id}`, input);
  },

  deleteSchema(id: string): Promise<DeletedResponse> {
    return api.del<DeletedResponse>(`${BASE}/schemas/${id}`);
  },

  /**
   * Runs one schema against one .docx and returns what came out, without saving anything.
   *
   * Multipart rather than JSON, so it goes through `fetchRaw`: `post()` would set a JSON
   * content-type and strip the boundary the browser generates for the FormData.
   *
   * There is no timeout here on purpose. A real extraction against a full submission takes
   * minutes, and the staff-bff route and submission-api both extend their deadlines to match —
   * anything shorter on this side would abandon a model call that has already been paid for.
   * Whatever is showing the spinner has to say how long it has been waiting.
   */
  async testSchema(id: string, file: File): Promise<AiSchemaTestResponse> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.fetchRaw(`${BASE}/schemas/${id}/test`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) throw await testFailure(response);

    return (await response.json()) as AiSchemaTestResponse;
  },

  listTestDocuments(search: string, limit = 20): Promise<AiTestDocumentListResponse> {
    const params = new URLSearchParams({ search, limit: String(limit) });
    return api.get<AiTestDocumentListResponse>(`${BASE}/test-documents?${params.toString()}`);
  },

  async testSchemaWithSubmission(
    id: string,
    submissionEntryFileId: string,
  ): Promise<AiSchemaTestResponse> {
    const response = await api.fetchRaw(`${BASE}/schemas/${id}/test-submission`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ submissionEntryFileId }),
    });

    if (!response.ok) throw await testFailure(response);

    return (await response.json()) as AiSchemaTestResponse;
  },

  /**
   * Uploads one .docx and runs the whole pipeline over it — every enabled schema, the markdown
   * templates, the blob writes and a real parse run per schema.
   *
   * Multipart rather than JSON, and so through `fetchRaw` for the same reason as `testSchema`:
   * `post()` would set a JSON content-type and strip the boundary the browser generates.
   *
   * It answers 202 as soon as the file is stored — the parsing happens on the worker afterwards,
   * and the runs it creates show up in the runs list with their live progress. That is the whole
   * difference from `testSchema`, which blocks for the length of one synchronous model call and
   * saves nothing.
   */
  async uploadManualDocument(
    file: File,
    fields: AiParseManualUploadFields = {},
  ): Promise<AiParseManualUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);
    // Blank strings are left out rather than sent: an untouched optional field must reach the API
    // as "not given", not as a publication whose name is the empty string.
    for (const key of ["publicationName", "edition", "submissionType"] as const) {
      const value = fields[key]?.trim();
      if (value) formData.append(key, value);
    }

    const response = await api.fetchRaw(`${BASE}/manual-uploads`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      // The server's own sentence where there is one. The 409 for a disabled worker explains the
      // kill switch and what to do about it, and `HTTP 409` explains nothing.
      const message = await response
        .json()
        .then(body => (body as { error?: string }).error)
        .catch(() => undefined);
      throw new Error(message || `Upload failed: HTTP ${response.status}`);
    }

    return (await response.json()) as AiParseManualUploadResponse;
  },

  listMarkdownTemplates(filters?: {
    aiSchemaId?: string;
  }): Promise<AiMarkdownTemplateListResponse> {
    const query = filters?.aiSchemaId
      ? `?aiSchemaId=${encodeURIComponent(filters.aiSchemaId)}`
      : "";
    return api.get<AiMarkdownTemplateListResponse>(`${BASE}/markdown-templates${query}`);
  },

  getMarkdownTemplateById(id: string): Promise<AiMarkdownTemplateSingleResponse> {
    return api.get<AiMarkdownTemplateSingleResponse>(`${BASE}/markdown-templates/${id}`);
  },

  createMarkdownTemplate(
    input: CreateAiMarkdownTemplateInput,
  ): Promise<AiMarkdownTemplateSingleResponse> {
    return api.post<AiMarkdownTemplateSingleResponse>(`${BASE}/markdown-templates`, input);
  },

  updateMarkdownTemplate(
    id: string,
    input: UpdateAiMarkdownTemplateInput,
  ): Promise<AiMarkdownTemplateSingleResponse> {
    return api.patch<AiMarkdownTemplateSingleResponse>(`${BASE}/markdown-templates/${id}`, input);
  },

  deleteMarkdownTemplate(id: string): Promise<DeletedResponse> {
    return api.del<DeletedResponse>(`${BASE}/markdown-templates/${id}`);
  },

  listRuns(filters?: AiParseRunFilters): Promise<AiParseRunListResponse> {
    return api.get<AiParseRunListResponse>(`${BASE}/runs${runFilterParams(filters, true)}`);
  },

  getRunStats(filters?: AiParseRunFilters): Promise<AiParseRunStatsResponse> {
    return api.get<AiParseRunStatsResponse>(`${BASE}/runs/stats${runFilterParams(filters, false)}`);
  },

  getRunById(id: string): Promise<AiParseRunSingleResponse> {
    return api.get<AiParseRunSingleResponse>(`${BASE}/runs/${id}`);
  },

  /**
   * Re-queues the one document this run was for, against the one schema it used.
   *
   * Scoped to that single schema deliberately, and it runs the schema even when the schema is
   * currently disabled — naming a run explicitly is someone asking for it. The original run row is
   * untouched; the new attempt gets its own row, which is the point of pressing the button.
   */
  rerunRun(id: string): Promise<AiParseRerunResponse> {
    return api.post<AiParseRerunResponse>(`${BASE}/runs/${id}/rerun`);
  },

  /**
   * One schema over a date range or the last N submissions, capped per job by the API.
   *
   * With `dryRun: true` nothing is enqueued and the response carries the candidate count and the
   * token/cost estimate — that is the number the user confirms against.
   */
  bulkRerun(input: AiParseBulkRerunInput): Promise<AiParseBulkRerunResponse> {
    return api.post<AiParseBulkRerunResponse>(`${BASE}/reruns/bulk`, input);
  },
};
